import type { TranslationSchema } from './en';

/**
 * Sinhala (සිංහල).
 *
 * One deliberate exception: `report.sheet.*` stays in English in both locales.
 * Those are the column headings of the printed collection form, which is
 * pre-printed English stationery, and the server-side PDF renders them in
 * English. If the browser print view translated them, the same report would
 * look different depending on who printed it and how — so the document stays
 * fixed while the UI around it follows the user's language.
 */
export const si: TranslationSchema = {
  app: {
    name: 'තරංග ස්ටීල්',
    subtitle: 'එකතු කිරීමේ පද්ධතිය',
  },

  common: {
    save: 'සුරකින්න',
    cancel: 'අවලංගු කරන්න',
    close: 'වසන්න',
    add: 'එකතු කරන්න',
    edit: 'සංස්කරණය',
    delete: 'මකන්න',
    remove: 'ඉවත් කරන්න',
    search: 'සොයන්න',
    filter: 'පෙරහන',
    clear: 'හිස් කරන්න',
    apply: 'යොදන්න',
    back: 'ආපසු',
    next: 'ඊළඟ',
    previous: 'පෙර',
    confirm: 'තහවුරු කරන්න',
    print: 'මුද්‍රණය කරන්න',
    download: 'බාගත කරන්න',
    export: 'නිර්යාත කරන්න',
    import: 'ආයාත කරන්න',
    refresh: 'නැවුම් කරන්න',
    loading: 'පූරණය වෙමින්…',
    saving: 'සුරකිමින්…',
    noResults: 'ප්‍රතිඵල නැත',
    none: 'කිසිවක් නැත',
    all: 'සියල්ල',
    yes: 'ඔව්',
    no: 'නැත',
    active: 'සක්‍රීයයි',
    inactive: 'අක්‍රීයයි',
    status: 'තත්ත්වය',
    actions: 'ක්‍රියා',
    date: 'දිනය',
    from: 'සිට',
    to: 'දක්වා',
    today: 'අද',
    total: 'එකතුව',
    optional: 'අත්‍යවශ්‍ය නොවේ',
    required: 'අවශ්‍යයි',
    selectPlaceholder: 'තෝරන්න…',
    searchPlaceholder: 'සෙවීමට ටයිප් කරන්න…',
    unassigned: 'පවරා නැත',
    showing: 'මුළු {{total}} න් {{from}}–{{to}} දක්වා',
    page: 'පිටු {{pages}} න් {{page}}',
    retry: 'නැවත උත්සාහ කරන්න',
    somethingWentWrong: 'යම් දෝෂයක් සිදු විය',
    notFound: 'හමු නොවීය',
  },

  nav: {
    dashboard: 'උපකරණ පුවරුව',
    collect: 'මුදල් එකතු කිරීම',
    myCollections: 'මගේ එකතු කිරීම්',
    collections: 'එකතු කිරීම්',
    invoices: 'ඉන්වොයිස්',
    shops: 'ගනුදෙනුකරුවෝ',
    agents: 'නියෝජිතයෝ',
    routes: 'මාර්ග',
    reports: 'වාර්තා',
    users: 'පරිශීලකයෝ',
    auditLog: 'විගණන සටහන',
    banks: 'බැංකු',
    settings: 'සැකසුම්',
    masterData: 'මූලික දත්ත',
    logout: 'පිටවන්න',
    language: 'භාෂාව',
  },

  auth: {
    signIn: 'ඇතුල් වන්න',
    signingIn: 'ඇතුල් වෙමින්…',
    username: 'පරිශීලක නාමය',
    password: 'මුරපදය',
    currentPassword: 'වත්මන් මුරපදය',
    newPassword: 'නව මුරපදය',
    confirmPassword: 'නව මුරපදය තහවුරු කරන්න',
    changePassword: 'මුරපදය වෙනස් කරන්න',
    changePasswordTitle: 'නව මුරපදයක් තෝරන්න',
    mustChangePassword:
      'ඔබේ මුරපදය පරිපාලකයෙකු විසින් සකසා ඇත. ඉදිරියට යාමට පෙර නව මුරපදයක් තෝරන්න.',
    passwordChanged: 'මුරපදය යාවත්කාලීන විය. නැවත ඇතුල් වන්න.',
    welcome: 'ඉදිරියට යාමට ඇතුල් වන්න',
    invalidCredentials: 'පරිශීලක නාමය හෝ මුරපදය වැරදියි',
    accountDisabled: 'මෙම ගිණුම අක්‍රීය කර ඇත',
    sessionExpired: 'ඔබේ සැසිය කල් ඉකුත් වී ඇත. නැවත ඇතුල් වන්න.',
    passwordsDoNotMatch: 'මුරපද දෙක නොගැලපේ',
    passwordRules: 'අවම වශයෙන් අකුරු 8ක්, එයට අකුරක් සහ ඉලක්කමක් ඇතුළත් විය යුතුයි',
    signedInAs: 'ඇතුල් වී ඇත්තේ',
  },

  dashboard: {
    title: 'උපකරණ පුවරුව',
    totalInvoiced: 'මුළු ඉන්වොයිස් වටිනාකම',
    totalCollected: 'මුළු එකතු කළ මුදල',
    totalOutstanding: 'ලැබිය යුතු මුදල',
    collectionRate: 'එකතු කිරීමේ අනුපාතය',
    pendingInvoices: 'ගෙවීමට ඇති ඉන්වොයිස්',
    overdueInvoices: 'කල් ඉකුත් වූ ඉන්වොයිස්',
    overdueAmount: 'කල් ඉකුත් වූ මුදල',
    dailyCollection: 'දෛනික එකතු කිරීම',
    agentCollection: 'නියෝජිත අනුව එකතු කිරීම',
    topByPending: 'ලැබිය යුතු මුදල අනුව ප්‍රධාන ගනුදෙනුකරුවෝ',
    topByInvoiced: 'ඉන්වොයිස් වටිනාකම අනුව ප්‍රධාන ගනුදෙනුකරුවෝ',
    overdueAgeing: 'කල් ඉකුත් වූ කාල පරාසය',
    routeView: 'මාර්ග',
    worstOverdue: 'වැඩිම කලක් කල් ඉකුත් වූ',
    collectedToday: 'අද එකතු කළ',
    shops: 'ගනුදෙනුකරුවෝ',
    daysOverdue: 'දින {{count}}ක් කල් ඉකුත්',
    bucket: 'කල් ඉකුත් වූ දින',
    noCollections: 'මෙම කාලය තුළ එකතු කිරීම් නැත',
    last30Days: 'පසුගිය දින 30',
    last7Days: 'පසුගිය දින 7',
    thisMonth: 'මෙම මාසය',
  },

  shop: {
    title: 'ගනුදෙනුකරුවෝ',
    one: 'ගනුදෙනුකරු',
    new: 'නව ගනුදෙනුකරු',
    edit: 'ගනුදෙනුකරු සංස්කරණය',
    clientName: 'ගනුදෙනුකරුගේ නම',
    code: 'කේතය',
    contactPerson: 'සම්බන්ධ වන පුද්ගලයා',
    contactNumber: 'දුරකථන අංකය',
    address: 'ලිපිනය',
    addressLine: 'ලිපින පේළිය {{n}}',
    route: 'මාර්ගය',
    agent: 'නියෝජිත',
    outstanding: 'ලැබිය යුතු මුදල',
    openInvoices: 'විවෘත ඉන්වොයිස්',
    noRoute: 'මාර්ගයක් නැත',
    unassignedOnly: 'මාර්ගයකට අයත් නොවන',
    withOutstandingOnly: 'ලැබිය යුතු මුදල ඇති අය පමණි',
    created: 'ගනුදෙනුකරු එකතු කරන ලදී',
    updated: 'ගනුදෙනුකරු යාවත්කාලීන විය',
    empty: 'තවම ගනුදෙනුකරුවෝ නැත. පළමුවැන්නා එකතු කරන්න.',
    noRouteWarning:
      'මෙම ගනුදෙනුකරු මාර්ගයකට අයත් නොවේ, එබැවින් කිසිදු නියෝජිතයෙකුට මොවුන්ගේ ඉන්වොයිස් නොපෙනේ. මාර්ගයක් පවරන්න.',
    invoiceHistory: 'ඉන්වොයිස් ඉතිහාසය',
  },

  agent: {
    title: 'නියෝජිතයෝ',
    one: 'නියෝජිත',
    new: 'නව නියෝජිත',
    edit: 'නියෝජිත සංස්කරණය',
    name: 'නියෝජිතයාගේ නම',
    code: 'කේතය',
    contactNumber: 'දුරකථන අංකය',
    nic: 'ජා.හැ. අංකය',
    address: 'ලිපිනය',
    routes: 'මාර්ග',
    login: 'ජංගම පිවිසුම',
    hasLogin: 'පිවිසුම ඇත',
    noLogin: 'පිවිසුමක් නැත',
    createLogin: 'පිවිසුම සාදන්න',
    resetLogin: 'පිවිසුම යළි සකසන්න',
    loginHelp:
      'අත්‍යවශ්‍ය නොවේ. මෙම නියෝජිතයා ඇතුල් වන පිවිසුම සාදයි. පළමු වරට ඇතුල් වන විට මුරපදය වෙනස් කිරීමට ඉල්ලා සිටී.',
    loginSet: 'පිවිසුම සුරකින ලදී. නියෝජිතයා පළමු වරට ඇතුල් වන විට මුරපදය වෙනස් කළ යුතුයි.',
    created: 'නියෝජිත එකතු කරන ලදී',
    updated: 'නියෝජිත යාවත්කාලීන විය',
    empty: 'තවම නියෝජිතයෝ නැත.',
    deactivateWarning:
      'නියෝජිතයෙකු අක්‍රීය කිරීමෙන් ඔවුන්ගේ පිවිසුමද අක්‍රීය වී සියලු සැසි අවසන් වේ.',
  },

  route: {
    title: 'මාර්ග',
    one: 'මාර්ගය',
    new: 'නව මාර්ගය',
    edit: 'මාර්ගය සංස්කරණය',
    name: 'මාර්ගයේ නම',
    namePlaceholder: 'උදා: මහවැව සිට දිවුලපිටිය',
    description: 'විස්තරය',
    agent: 'පවරන ලද නියෝජිත',
    shopCount: 'ගනුදෙනුකරුවෝ',
    assignAgent: 'නියෝජිත පවරන්න',
    assignShops: 'ගනුදෙනුකරුවෝ එකතු කරන්න',
    removeShop: 'මාර්ගයෙන් ඉවත් කරන්න',
    noAgent: 'නියෝජිතයෙක් පවරා නැත',
    created: 'මාර්ගය සාදන ලදී',
    updated: 'මාර්ගය යාවත්කාලීන විය',
    agentAssigned: 'නියෝජිත පවරන ලදී. මෙම මාර්ගයේ ලැබිය යුතු ඉන්වොයිස් දැන් ඔවුන් වෙත යයි.',
    shopsAssigned: 'ගනුදෙනුකරුවෝ {{count}} දෙනෙක් මෙම මාර්ගයට එකතු කරන ලදී',
    shopsMoved: 'ඉන් {{count}} දෙනෙක් වෙනත් මාර්ගයකින් ගෙන එන ලදී',
    shopRemoved: 'ගනුදෙනුකරු මෙම මාර්ගයෙන් ඉවත් කරන ලදී',
    empty: 'තවම මාර්ග නැත.',
    mappingHelp:
      'ගනුදෙනුකරුවෙක් අයත් වන්නේ එක් මාර්ගයකට පමණි, මාර්ගයකට ඇත්තේ එක් නියෝජිතයෙකි. ඉන්වොයිස් ස්වයංක්‍රීයව මෙම මාර්ගය අනුගමනය කරයි.',
  },

  invoice: {
    title: 'ඉන්වොයිස්',
    one: 'ඉන්වොයිසය',
    new: 'නව ඉන්වොයිසය',
    edit: 'ඉන්වොයිසය සංස්කරණය',
    invoiceNo: 'ඉන්වොයිස් අංකය',
    customer: 'ගනුදෙනුකරු',
    description: 'විස්තරය',
    amount: 'ඉන්වොයිස් මුදල',
    invoiceDate: 'ඉන්වොයිස් දිනය',
    dueDate: 'නියමිත දිනය',
    outstanding: 'ලැබිය යුතු මුදල',
    collected: 'එකතු කළ මුදල',
    overdue: 'කල් ඉකුත්',
    overdueOnly: 'කල් ඉකුත් වූ පමණි',
    outstandingOnly: 'ලැබිය යුතු පමණි',
    created: 'ඉන්වොයිසය සාදන ලදී',
    updated: 'ඉන්වොයිසය යාවත්කාලීන විය',
    cancelled: 'ඉන්වොයිසය අවලංගු කරන ලදී',
    cancelTitle: 'මෙම ඉන්වොයිසය අවලංගු කරන්න',
    cancelReason: 'හේතුව',
    cancelWarning: 'මෙය නැවත හැරවිය නොහැක. ඉන්වොයිසය තවදුරටත් එකතු කළ නොහැක.',
    editFloorNotice: 'මෙම ඉන්වොයිසයට එරෙහිව {{amount}} දැනටමත් එකතු කර ඇත — මුදල එයට වඩා අඩු කළ නොහැක.',
    editReasonLabel: 'වෙනස් කිරීමට හේතුව',
    editReasonHint: 'මෙම ඉන්වොයිසයට එරෙහිව දැනටමත් එකතු කිරීම් ඇති බැවින් මෙය අවශ්‍යයි.',
    moveReasonHint: 'ඉන්වොයිසයක් වෙනත් පාරිභෝගිකයෙකු වෙත ගෙන යන විට මෙය අවශ්‍යයි.',
    moveShopNotice:
      'මෙමගින් ඉන්වොයිසය වෙනත් පාරිභෝගිකයෙකු සහ ඔවුන්ගේ නියෝජිතයා වෙත ගෙන යයි. නියෝජිතයින්ට මෙම වෙනස පෙනෙන්නේ ඔවුන්ගේ යෙදුම ඊළඟ වතාවේ සමමුහුර්ත වූ විටය.',
    routedTo: 'යොමු කර ඇත්තේ',
    collectionHistory: 'මෙම ඉන්වොයිසයට එරෙහි එකතු කිරීම්',
    empty: 'තවම ඉන්වොයිස් නැත.',
    status: {
      OPEN: 'විවෘත',
      PARTIALLY_PAID: 'කොටසක් ගෙවා ඇත',
      PAID: 'ගෙවා ඇත',
      CANCELLED: 'අවලංගුයි',
    },
    import: {
      title: 'ඉන්වොයිස් ආයාත කරන්න',
      selectFile: 'CSV ගොනුවක් තෝරන්න',
      dryRun: 'පරීක්ෂා කරන්න පමණි, ආයාත නොකරන්න',
      allowPartial: 'වලංගු පේළි ආයාත කර වැරදි ඒවා මඟ හරින්න',
      help: 'තීරු: invoice_no, shop_code (හෝ shop_name), description, amount, invoice_date, due_date',
      validating: 'පරීක්ෂා කරමින්…',
      importing: 'ආයාත කරමින්…',
      resultTitle: 'ආයාත ප්‍රතිඵලය',
      totalRows: 'කියවූ පේළි',
      validRows: 'වලංගු',
      imported: 'ආයාත කළ',
      skipped: 'මඟ හැරි',
      rowErrors: 'පේළි දෝෂ',
      row: 'පේළිය {{row}}',
      allOrNothing:
        'පෙරනිමියෙන්, කිසියම් පේළියක් වැරදි නම් කිසිවක් ආයාත නොවේ. ගොනුව නිවැරදි කරන්න, නැතහොත් ඉහත විකල්පය තෝරන්න.',
      success: 'ඉන්වොයිස් {{count}}ක් ආයාත කරන ලදී',
      dryRunSuccess: 'ගොනුව වලංගුයි. ඉන්වොයිස් {{count}}ක් ආයාත කිරීමට සූදානම්.',
    },
  },

  collection: {
    title: 'එකතු කිරීම්',
    one: 'එකතු කිරීම',
    new: 'මුදල් එකතු කිරීමක් සටහන් කරන්න',
    receiptNo: 'කුවිතාන්සි අංකය',
    customer: 'ගනුදෙනුකරු',
    agent: 'නියෝජිත',
    collectedAt: 'එකතු කළ වේලාව',
    collectionDate: 'එකතු කළ දිනය',
    cash: 'මුදල්',
    cheque: 'චෙක්පත',
    chequeNo: 'චෙක්පත් අංකය',
    chequeDate: 'චෙක්පත් දිනය',
    bank: 'බැංකුව',
    amount: 'මුදල',
    method: 'ක්‍රමය',
    notes: 'සටහන්',
    payments: 'එකතු කළ මුදල්',
    addCash: 'මුදල් එකතු කරන්න',
    addCheque: 'චෙක්පතක් එකතු කරන්න',
    allocations: 'ඉන්වොයිස් වලට බෙදන්න',
    autoAllocate: 'පැරණි ඒවාට මුලින්ම බෙදන්න',
    clearAllocations: 'හිස් කරන්න',
    collected: 'එකතු කළ',
    assigned: 'බෙදූ',
    remaining: 'තව බෙදිය යුතු',
    overAssigned: 'වැඩියෙන් බෙදා ඇත',
    balanced: 'සමතුලිතයි',
    issueReceipt: 'කුවිතාන්සිය නිකුත් කරන්න',
    issuing: 'නිකුත් කරමින්…',
    issued: 'කුවිතාන්සිය {{receiptNo}} නිකුත් කරන ලදී',
    selectCustomer: 'ගනුදෙනුකරුවෙකු තෝරන්න',
    selectCustomerFirst: 'ගෙවීමට ඇති ඉන්වොයිස් බැලීමට ගනුදෙනුකරුවෙකු තෝරන්න',
    noPendingInvoices: 'මෙම ගනුදෙනුකරුට ගෙවීමට ඇති ඉන්වොයිස් නැත.',
    pendingInvoices: 'ගෙවීමට ඇති ඉන්වොයිස්',
    payFull: 'සම්පූර්ණයෙන් ගෙවන්න',
    slip: 'ගනුදෙනුකරු පත්‍රිකාව',
    printSlip: 'පත්‍රිකාව මුද්‍රණය කරන්න',
    newCollection: 'තවත් එකක් සටහන් කරන්න',
    void: 'කුවිතාන්සිය අවලංගු කරන්න',
    voidTitle: 'මෙම කුවිතාන්සිය අවලංගු කරන්න',
    voidReason: 'හේතුව',
    voidWarning:
      'මෙය මෙම කුවිතාන්සිය මගින් බෙදූ සියලු මුදල් ආපසු හරවා එම ඉන්වොයිස් නැවත විවෘත කරයි. කුවිතාන්සිය අවලංගු ලෙස සලකුණු කර තබා ගනී.',
    voided: 'කුවිතාන්සිය අවලංගු කරන ලදී',
    voidedBadge: 'අවලංගුයි',
    empty: 'තවම එකතු කිරීම් සටහන් කර නැත.',
    duplicateSubmission: 'මෙම එකතු කිරීම දැනටමත් සටහන් කර ඇත — මුල් කුවිතාන්සිය පෙන්වයි.',
    issues: {
      NO_PAYMENTS: 'අවම වශයෙන් මුදල් හෝ චෙක්පත් ගෙවීමක් සටහන් කරන්න',
      INVALID_PAYMENT_AMOUNT: 'බිංදුවට වඩා වැඩි මුදලක් ඇතුළත් කරන්න',
      CHEQUE_DETAILS_REQUIRED: 'චෙක්පතකට එහි අංකය, දිනය සහ බැංකුව අවශ්‍යයි',
      DUPLICATE_CHEQUE: 'මෙම චෙක්පත දැනටමත් මෙම කුවිතාන්සියේ ඇතුළත් කර ඇත',
      NO_ALLOCATIONS: 'එකතු කළ මුදල අවම වශයෙන් එක් ඉන්වොයිසයකට හෝ බෙදන්න',
      ALLOCATION_EXCEEDS_OUTSTANDING:
        'ඉන්වොයිසය {{invoiceNo}}: ලැබිය යුතු මුදලට වඩා වැඩියෙන් බෙදිය නොහැක',
      UNDER_ALLOCATED: 'එකතු කළ මුදලින් රු. {{amount}}ක් තවම බෙදා නැත',
      OVER_ALLOCATED: 'එකතු කළ මුදලට වඩා රු. {{amount}}ක් වැඩියෙන් බෙදා ඇත',
    },
  },

  report: {
    title: 'වාර්තා',
    agentDaily: 'නියෝජිත දෛනික විස්තර',
    agentDailyDesc: 'එක් නියෝජිතයෙකුගේ එක් දිනක සම්පූර්ණ එකතු කිරීමේ පත්‍රය',
    agentTotals: 'නියෝජිත එකතුව',
    agentTotalsDesc: 'දිනකදී එක් එක් නියෝජිතයා එකතු කළ මුදල',
    shopBreakdown: 'ගනුදෙනුකරු අනුව විස්තරය',
    shopBreakdownDesc: 'ගනුදෙනුකරු අනුව දෛනික එකතු කිරීම්',
    summary: 'එකතු කිරීමේ සාරාංශය',
    summaryDesc: 'දින පරාසයක් තුළ දෛනික එකතුව',
    routeDaily: 'මාර්ග අනුව එකතු කිරීම්',
    routeDailyDesc: 'දිනක එකතු කිරීම් මාර්ග අනුව',
    cheques: 'චෙක්පත් ලේඛනය',
    chequesDesc: 'දින පරාසයක් තුළ ලැබුණු සියලු චෙක්පත්',
    selectAgent: 'නියෝජිත',
    selectDate: 'දිනය',
    downloadPdf: 'PDF බාගත කරන්න',
    printReport: 'මුද්‍රණය කරන්න',
    generating: 'සකසමින්…',
    noData: 'මෙම තේරීම සඳහා එකතු කිරීම් නැත.',
    // Kept in English deliberately — see the note at the top of this file.
    sheet: {
      heading: 'COLLECTION SUMMARY REPORTS',
      date: 'Date',
      estName: 'M.Est. Name',
      serialNo: 'Serial No',
      receiptNo: 'Receipt No',
      customerName: 'Customer Name',
      invoiceNo: 'Invoice No.',
      invoiceDate: 'Invoice Date',
      createdDate: 'Created Date',
      invoiceAmount: 'Invoice Amount',
      cash: 'Cash',
      cheque: 'Cheque',
      chequeNo: 'Cheque No.',
      chequeDate: 'Cheque Date',
      bank: 'Bank',
      total: 'Total',
      collectedBy: 'Collected by (Agent)',
      checkedBy: 'Checked by',
      approvedBy: 'Approved by',
    },
    columns: {
      receipts: 'කුවිතාන්සි',
      customers: 'ගනුදෙනුකරුවෝ',
      cash: 'මුදල්',
      cheque: 'චෙක්පත්',
      total: 'එකතුව',
    },
  },

  user: {
    title: 'පරිශීලකයෝ',
    one: 'පරිශීලක',
    new: 'නව පරිශීලක',
    edit: 'පරිශීලක සංස්කරණය',
    username: 'පරිශීලක නාමය',
    fullName: 'සම්පූර්ණ නම',
    role: 'භූමිකාව',
    lastLogin: 'අවසන් පිවිසුම',
    resetPassword: 'මුරපදය යළි සකසන්න',
    passwordReset: 'මුරපදය යළි සකසන ලදී. පරිශීලකයා ඊළඟ පිවිසුමේදී එය වෙනස් කළ යුතුයි.',
    created: 'පරිශීලක එකතු කරන ලදී',
    updated: 'පරිශීලක යාවත්කාලීන විය',
    agentLoginNote: 'නියෝජිත පිවිසුම් සාදනු ලබන්නේ නියෝජිත වාර්තාවේ මිස මෙහි නොවේ.',
    roles: {
      ADMIN: 'පරිපාලක',
      FINANCE: 'මූල්‍ය',
      AGENT: 'නියෝජිත',
    },
  },

  auditLog: {
    title: 'විගණන සටහන',
    description: 'කවුරුන් විසින් කුමක් කළේද, කවදාද — ඉන්වොයිස් සෑදීම, ඉන්වොයිස් සංස්කරණය සහ වාර්තා බාගැනීම්.',
    dateTime: 'දිනය සහ වේලාව',
    user: 'පරිශීලක',
    action: 'ක්‍රියාව',
    details: 'විස්තර',
    allActions: 'සියලුම ක්‍රියා',
    allUsers: 'සියලුම පරිශීලකයෝ',
    system: 'පද්ධතිය',
    actions: {
      invoiceCreated: 'ඉන්වොයිස් සෑදිණි',
      invoiceUpdated: 'ඉන්වොයිස් සංස්කරණය විය',
      reportDownloaded: 'වාර්තාව බාගත කරන ලදී',
    },
  },

  bank: {
    title: 'බැංකු',
    one: 'බැංකුව',
    new: 'නව බැංකුව',
    name: 'බැංකුවේ නම',
    shortCode: 'කෙටි කේතය',
    shortCodeHelp: 'වාර්තාවේ බැංකු තීරුවේ මුද්‍රණය වේ, උදා: BOC, P/B',
    created: 'බැංකුව එකතු කරන ලදී',
    updated: 'බැංකුව යාවත්කාලීන විය',
  },

  errors: {
    network: 'සේවාදායකයට සම්බන්ධ විය නොහැක. ඔබේ සම්බන්ධතාවය පරීක්ෂා කරන්න.',
    unauthorized: 'කරුණාකර නැවත ඇතුල් වන්න.',
    forbidden: 'එය කිරීමට ඔබට අවසර නැත.',
    notFound: 'එම වාර්තාව තවදුරටත් නොපවතී.',
    conflict: 'එය පවතින දත්ත සමඟ ගැටේ.',
    validation: 'කරුණාකර සලකුණු කළ ක්ෂේත්‍ර පරීක්ෂා කරන්න.',
    server: 'සේවාදායකයේ ගැටලුවක් ඇති විය. මොහොතකින් නැවත උත්සාහ කරන්න.',
    unknown: 'යම් දෝෂයක් සිදු විය.',
    pageNotFound: 'එම පිටුව නොපවතී.',
    goHome: 'උපකරණ පුවරුවට යන්න',
  },
};
